import React, { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export type DialogBackdropPolicy = 'dismiss' | 'static';

type AccessibleName =
  | { ariaLabel: string; labelledBy?: never }
  | { ariaLabel?: never; labelledBy: string };

interface DialogShellBaseProps {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  backdropPolicy: DialogBackdropPolicy;
  describedBy?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  overlayClassName?: string;
  panelClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
}

export type DialogShellProps = DialogShellBaseProps & AccessibleName;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const DialogNestingDepth = React.createContext(0);

type BackgroundState = { inert: boolean; ariaHidden: string | null };

interface DialogDocumentManager {
  activePanels: Set<HTMLElement>;
  previousBodyOverflow: string | null;
  backgroundObserver: MutationObserver | null;
  backgroundStates: Map<HTMLElement, BackgroundState>;
  restoreTargets: WeakMap<HTMLElement, HTMLElement | null>;
  pendingRestoreTarget: { target: HTMLElement; depth: number } | null;
}

// The coordinator belongs to the Document, not this module. That gives every
// React root one shared lock owner and lets Fast Refresh reuse the live state
// instead of resetting module counters while mounted dialogs still exist.
const DIALOG_MANAGER_KEY = Symbol.for('bark.dialog-shell.manager');
type ManagedDocument = Document & { [key: symbol]: DialogDocumentManager | undefined };

function dialogManager(ownerDocument: Document): DialogDocumentManager {
  const managedDocument = ownerDocument as ManagedDocument;
  if (!managedDocument[DIALOG_MANAGER_KEY]) {
    managedDocument[DIALOG_MANAGER_KEY] = {
      activePanels: new Set(),
      previousBodyOverflow: null,
      backgroundObserver: null,
      backgroundStates: new Map(),
      restoreTargets: new WeakMap(),
      pendingRestoreTarget: null,
    };
  }
  return managedDocument[DIALOG_MANAGER_KEY] as DialogDocumentManager;
}

function isFocusable(element: HTMLElement): boolean {
  if (element.tabIndex < 0 || element.matches(':disabled')) return false;
  if (element.closest('[inert], [hidden], [aria-hidden="true"]')) return false;

  const style = element.ownerDocument.defaultView?.getComputedStyle(element);
  if (!style) return true;
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function focusableElements(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusable);
}

function topmostDialogPanel(ownerDocument: Document): HTMLElement | null {
  const panels = ownerDocument.querySelectorAll<HTMLElement>(
    '[data-dialog-shell="panel"]:not([data-dialog-closing="true"])',
  );
  let topmost: HTMLElement | null = null;
  let greatestDepth = -1;

  panels.forEach((panel) => {
    const depth = Number(panel.dataset.dialogDepth ?? 0);
    if (depth >= greatestDepth) {
      greatestDepth = depth;
      topmost = panel;
    }
  });

  return topmost;
}

function synchronizeDialogExposure(ownerDocument: Document): void {
  const topmostPanel = topmostDialogPanel(ownerDocument);
  ownerDocument.querySelectorAll<HTMLElement>('[data-dialog-shell="backdrop"]').forEach((backdrop) => {
    const panel = backdrop.firstElementChild;
    const isTopmost = panel === topmostPanel;
    if (isTopmost) {
      backdrop.removeAttribute('inert');
      backdrop.removeAttribute('aria-hidden');
    } else {
      backdrop.setAttribute('inert', '');
      backdrop.setAttribute('aria-hidden', 'true');
    }
  });
}

function releaseDocumentLocks(
  ownerDocument: Document,
  manager: DialogDocumentManager,
): void {
  manager.backgroundObserver?.disconnect();
  manager.backgroundObserver = null;
  manager.backgroundStates.forEach((state, element) => {
    if (state.inert) element.setAttribute('inert', '');
    else element.removeAttribute('inert');

    if (state.ariaHidden === null) element.removeAttribute('aria-hidden');
    else element.setAttribute('aria-hidden', state.ariaHidden);
  });
  manager.backgroundStates.clear();

  if (manager.previousBodyOverflow !== null) {
    ownerDocument.body.style.overflow = manager.previousBodyOverflow;
    manager.previousBodyOverflow = null;
  }
}

function pruneDisconnectedPanels(
  ownerDocument: Document,
  manager: DialogDocumentManager,
): void {
  // A failed test or refresh can replace the portal tree before an old cleanup
  // runs. Identity-based pruning keeps that stale registration from poisoning
  // the next mount; unlike a counter, duplicate/late cleanup cannot underflow.
  manager.activePanels.forEach((panel) => {
    if (!panel.isConnected) manager.activePanels.delete(panel);
  });
  if (manager.activePanels.size === 0) {
    releaseDocumentLocks(ownerDocument, manager);
    manager.pendingRestoreTarget = null;
  }
}

function lockDocumentForPanel(ownerDocument: Document, panel: HTMLElement): () => boolean {
  const manager = dialogManager(ownerDocument);
  pruneDisconnectedPanels(ownerDocument, manager);

  const hideBackgroundElement = (element: HTMLElement) => {
    if (element.dataset.dialogShell === 'backdrop' || manager.backgroundStates.has(element)) return;

    manager.backgroundStates.set(element, {
      inert: element.hasAttribute('inert'),
      ariaHidden: element.getAttribute('aria-hidden'),
    });
    element.setAttribute('inert', '');
    element.setAttribute('aria-hidden', 'true');
  };

  if (manager.activePanels.size === 0) {
    manager.previousBodyOverflow = ownerDocument.body.style.overflow;
    ownerDocument.body.style.overflow = 'hidden';

    Array.from(ownerDocument.body.children).forEach((element) => {
      if (element instanceof HTMLElement) hideBackgroundElement(element);
    });

    const MutationObserverConstructor = ownerDocument.defaultView?.MutationObserver;
    manager.backgroundObserver = MutationObserverConstructor
      ? new MutationObserverConstructor((records) => {
        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) hideBackgroundElement(node);
          });
        });
      })
      : null;
    manager.backgroundObserver?.observe(ownerDocument.body, { childList: true });
  }
  manager.activePanels.add(panel);

  return () => {
    manager.activePanels.delete(panel);
    if (manager.activePanels.size !== 0) return false;

    releaseDocumentLocks(ownerDocument, manager);
    return true;
  };
}

/**
 * Shared accessible foundation for BARK modal overlays.
 *
 * Callers own their content and layout. DialogShell owns the scrim, semantic
 * dialog contract, focus lifecycle, Escape/backdrop dismissal, and body scroll
 * lock. Every caller must choose a backdrop policy explicitly so forms cannot
 * silently discard typed input.
 */
export default function DialogShell({
  open,
  onClose,
  children,
  backdropPolicy,
  ariaLabel,
  labelledBy,
  describedBy,
  initialFocusRef,
  header,
  footer,
  overlayClassName = '',
  panelClassName = '',
  headerClassName = '',
  bodyClassName = 'contents',
  footerClassName = '',
}: DialogShellProps) {
  const nestingDepth = React.useContext(DialogNestingDepth);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const latestInitialFocusRef = useRef(initialFocusRef);
  const backdropPointerStartedRef = useRef<boolean | null>(null);
  const backdropPointerEndedRef = useRef<boolean | null>(null);
  onCloseRef.current = onClose;
  latestInitialFocusRef.current = initialFocusRef;

  useLayoutEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    if (!panel) return;
    const ownerDocument = panel.ownerDocument;
    const manager = dialogManager(ownerDocument);
    panel.removeAttribute('data-dialog-closing');
    synchronizeDialogExposure(ownerDocument);

    const activeElement = ownerDocument.activeElement;
    const activeTarget = activeElement instanceof HTMLElement ? activeElement : null;
    const topmostPanel = topmostDialogPanel(ownerDocument);
    const inheritedRestoreTarget =
      topmostPanel && topmostPanel !== panel
        ? manager.restoreTargets.get(topmostPanel)
        : undefined;
    restoreFocusRef.current =
      inheritedRestoreTarget !== undefined ? inheritedRestoreTarget : activeTarget;
    manager.restoreTargets.set(panel, restoreFocusRef.current);
    const unlockDocument = lockDocumentForPanel(ownerDocument, panel);

    const requestedInitialFocus = latestInitialFocusRef.current?.current;
    const defaultInitialFocus = panel.querySelector<HTMLElement>('[data-dialog-initial-focus]');
    const firstFocusable = focusableElements(panel)[0];
    const initialFocus =
      requestedInitialFocus && panel.contains(requestedInitialFocus) && isFocusable(requestedInitialFocus)
        ? requestedInitialFocus
        : defaultInitialFocus && isFocusable(defaultInitialFocus)
          ? defaultInitialFocus
          : firstFocusable ?? panel;
    if (topmostDialogPanel(ownerDocument) === panel) initialFocus.focus();
    let lastFocusedInside =
      ownerDocument.activeElement instanceof HTMLElement && panel.contains(ownerDocument.activeElement)
        ? ownerDocument.activeElement
        : initialFocus;

    const repairFocus = () => {
      if (topmostDialogPanel(ownerDocument) !== panel) return;

      const current = ownerDocument.activeElement;
      if (current === panel) return;
      if (current instanceof HTMLElement && panel.contains(current) && isFocusable(current)) {
        lastFocusedInside = current;
        return;
      }

      const recoveryTarget =
        lastFocusedInside.isConnected && panel.contains(lastFocusedInside) && isFocusable(lastFocusedInside)
          ? lastFocusedInside
          : focusableElements(panel)[0] ?? panel;
      recoveryTarget.focus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (topmostDialogPanel(ownerDocument) !== panel) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = focusableElements(panel);
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = ownerDocument.activeElement;

      if (event.shiftKey && (current === first || !panel.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !panel.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (topmostDialogPanel(ownerDocument) !== panel) return;

      const target = event.target;
      if (target === panel) return;
      if (target instanceof HTMLElement && panel.contains(target) && isFocusable(target)) {
        lastFocusedInside = target;
        return;
      }

      repairFocus();
    };

    const focusObserver = new MutationObserver(repairFocus);
    focusObserver.observe(panel, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'disabled', 'hidden', 'inert', 'style'],
    });

    ownerDocument.addEventListener('keydown', handleKeyDown, true);
    ownerDocument.addEventListener('focusin', handleFocusIn, true);

    return () => {
      ownerDocument.removeEventListener('keydown', handleKeyDown, true);
      ownerDocument.removeEventListener('focusin', handleFocusIn, true);
      focusObserver.disconnect();
      panel.setAttribute('data-dialog-closing', 'true');
      synchronizeDialogExposure(ownerDocument);
      manager.restoreTargets.delete(panel);

      const restoreTarget = restoreFocusRef.current;
      if (
        restoreTarget?.isConnected &&
        (!manager.pendingRestoreTarget || nestingDepth <= manager.pendingRestoreTarget.depth)
      ) {
        manager.pendingRestoreTarget = { target: restoreTarget, depth: nestingDepth };
      }

      const backgroundReleased = unlockDocument();
      if (backgroundReleased) {
        const finalRestoreTarget = manager.pendingRestoreTarget?.target ?? null;
        manager.pendingRestoreTarget = null;
        if (
          finalRestoreTarget?.isConnected &&
          (finalRestoreTarget.dataset.dialogShell === 'panel' || isFocusable(finalRestoreTarget))
        ) {
          finalRestoreTarget.focus();
        }
      } else if (
        restoreTarget?.isConnected &&
        (restoreTarget.dataset.dialogShell === 'panel' || isFocusable(restoreTarget))
      ) {
        restoreTarget.focus();
        if (
          ownerDocument.activeElement === restoreTarget &&
          manager.pendingRestoreTarget?.target === restoreTarget
        ) {
          manager.pendingRestoreTarget = null;
        }
      }

      queueMicrotask(() => {
        const remainingDialog = topmostDialogPanel(ownerDocument);
        if (!remainingDialog) return;

        const current = ownerDocument.activeElement;
        if (current === remainingDialog) return;
        if (
          current instanceof HTMLElement &&
          remainingDialog.contains(current) &&
          isFocusable(current)
        ) {
          return;
        }

        (focusableElements(remainingDialog)[0] ?? remainingDialog).focus();
      });
    };
  }, [nestingDepth, open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-dialog-shell="backdrop"
      className={`dialog-shell-backdrop fixed inset-0 ${overlayClassName}`.trim()}
      style={{
        zIndex: `calc(var(--z-modal) + ${nestingDepth})`,
      }}
      onPointerDown={(event) => {
        const beganOnBackdrop = event.target === event.currentTarget;
        backdropPointerStartedRef.current = beganOnBackdrop;
        backdropPointerEndedRef.current = null;
        if (beganOnBackdrop) event.preventDefault();
      }}
      onPointerUp={(event) => {
        backdropPointerEndedRef.current = event.target === event.currentTarget;
      }}
      onPointerCancel={() => {
        backdropPointerStartedRef.current = false;
        backdropPointerEndedRef.current = false;
      }}
      onClick={(event) => {
        const beganOnBackdrop = backdropPointerStartedRef.current;
        const endedOnBackdrop = backdropPointerEndedRef.current;
        backdropPointerStartedRef.current = null;
        backdropPointerEndedRef.current = null;
        const directPointerClick = beganOnBackdrop === true && endedOnBackdrop === true;
        const nonPointerClick = beganOnBackdrop === null && endedOnBackdrop === null;
        if (
          event.target === event.currentTarget &&
          (directPointerClick || nonPointerClick) &&
          backdropPolicy === 'dismiss'
        ) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        data-dialog-shell="panel"
        data-dialog-depth={nestingDepth}
        className={`dialog-shell-panel focus:outline-none ${panelClassName}`.trim()}
      >
        <DialogNestingDepth.Provider value={nestingDepth + 1}>
          {header !== undefined && (
            <div data-dialog-slot="header" className={headerClassName}>{header}</div>
          )}
          {children !== undefined && (
            <div data-dialog-slot="body" className={bodyClassName}>{children}</div>
          )}
          {footer !== undefined && (
            <div data-dialog-slot="footer" className={footerClassName}>{footer}</div>
          )}
        </DialogNestingDepth.Provider>
      </div>
    </div>,
    document.body,
  );
}
